import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { recordPatientSubmission } from './InterestTracker';

export default function PrivatePayForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    phone: '',
    email: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    transportationType: '',
    frequency: '',
    roundTrip: '',
    pickupAddress: '',
    dropoffAddress: '',
    notesForDriver: '',
    currentPrice: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.phone || !formData.email || !formData.transportationType) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('city', formData.city);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('emergencyContactName', formData.emergencyContactName);
      formBody.append('emergencyContactPhone', formData.emergencyContactPhone);
      formBody.append('emergencyContactRelationship', formData.emergencyContactRelationship);
      formBody.append('transportationType', formData.transportationType);
      formBody.append('frequency', formData.frequency);
      formBody.append('roundTrip', formData.roundTrip);
      formBody.append('pickupAddress', formData.pickupAddress);
      formBody.append('dropoffAddress', formData.dropoffAddress);
      formBody.append('notesForDriver', formData.notesForDriver);
      formBody.append('currentPrice', formData.currentPrice);

      const response = await fetch('https://readdy.ai/api/form/d6ocln130mftb86r3i5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      if (response.ok) {
        recordPatientSubmission(formData.city);
        navigate('/thank-you?form=private-pay');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isGurney = formData.transportationType === 'Gurney / Stretcher';

  return (
    <section id="private-pay-form" className="py-24 bg-gradient-to-b from-gray-100 to-gray-200">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-yellow-500/10 text-yellow-600 border border-yellow-500/30 rounded-full px-5 py-2 mb-6">
            <i className="ri-user-heart-line text-lg"></i>
            <span className="font-semibold text-sm whitespace-nowrap">For Individual Patients</span>
          </div>
          <h3 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Private Pay Transportation Interest
          </h3>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            If you or a loved one may need transportation to medical appointments in the future, join our early access list.
          </p>
        </div>

        <form onSubmit={handleSubmit} id="private-pay-form-element" data-readdy-form className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-gray-200">

          {/* Basic Info */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Name <span className="text-yellow-600">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                City
              </label>
              <select
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent cursor-pointer"
              >
                <option value="">Select city</option>
                <option value="Sun City">Sun City</option>
                <option value="Sun City West">Sun City West</option>
                <option value="Peoria">Peoria</option>
                <option value="Surprise">Surprise</option>
                <option value="Goodyear">Goodyear</option>
                <option value="Glendale">Glendale</option>
                <option value="Scottsdale">Scottsdale</option>
                <option value="Other West Valley">Other West Valley</option>
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Phone <span className="text-yellow-600">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email <span className="text-yellow-600">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="your@email.com"
              />
            </div>
          </div>

          {/* Transport Type — moved up & required */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              What type of transportation would you need? <span className="text-yellow-600">*</span>
            </label>
            <select
              name="transportationType"
              value={formData.transportationType}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent cursor-pointer"
            >
              <option value="">Select type</option>
              <option value="Ambulatory">Ambulatory (walking with or without minor assistance)</option>
              <option value="Wheelchair">Wheelchair</option>
              <option value="Gurney / Stretcher">Gurney / Stretcher (patient must remain lying flat)</option>
            </select>

            {/* Gurney callout */}
            {isGurney && (
              <div className="mt-3 flex gap-3 bg-yellow-50 border border-yellow-400 rounded-xl px-4 py-3">
                <div className="w-5 h-5 flex items-center justify-center text-yellow-600 flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-base"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-yellow-800 mb-0.5">Gurney / Stretcher Transport Note</p>
                  <p className="text-xs text-yellow-700 leading-relaxed">
                    Gurney transport requires a <strong>two-person crew</strong> and specialized vehicle equipment. The base rate for stretcher transport is <strong>$275</strong>, plus $3.50 per mile. Our team will confirm crew availability and details when we follow up.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Emergency Contact */}
          <div className="mb-6 p-5 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-5 h-5 flex items-center justify-center text-red-500">
                <i className="ri-shield-user-line text-base"></i>
              </div>
              <span className="text-sm font-bold text-red-500 uppercase tracking-wide">Emergency Contact</span>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Emergency Contact Name
                </label>
                <input
                  type="text"
                  name="emergencyContactName"
                  value={formData.emergencyContactName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Emergency Contact Phone
                </label>
                <input
                  type="tel"
                  name="emergencyContactPhone"
                  value={formData.emergencyContactPhone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Relationship to Patient
              </label>
              <select
                name="emergencyContactRelationship"
                value={formData.emergencyContactRelationship}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent cursor-pointer"
              >
                <option value="">Select relationship</option>
                <option value="Spouse">Spouse</option>
                <option value="Parent">Parent</option>
                <option value="Child">Child</option>
                <option value="Sibling">Sibling</option>
                <option value="Friend">Friend</option>
                <option value="Caregiver">Caregiver</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              How often would rides be needed?
            </label>
            <select
              name="frequency"
              value={formData.frequency}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent cursor-pointer"
            >
              <option value="">Select frequency</option>
              <option value="One-time">One-time</option>
              <option value="Weekly">Weekly</option>
              <option value="Dialysis (3x per week)">Dialysis (3x per week)</option>
              <option value="Occasional appointments">Occasional appointments</option>
            </select>
          </div>

          {/* Round Trip */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Will this be a round trip ride?
            </label>
            <div className="flex gap-6">
              {['Yes', 'No', 'Sometimes'].map((option) => (
                <label key={option} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="roundTrip"
                    value={option}
                    checked={formData.roundTrip === option}
                    onChange={handleChange}
                    className="w-4 h-4 text-yellow-500 border-gray-300 bg-gray-50 focus:ring-2 focus:ring-yellow-500 cursor-pointer"
                  />
                  <span className="text-gray-600 text-sm">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Pickup & Drop-off Addresses */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Pickup Address
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-yellow-600">
                <i className="ri-map-pin-2-line text-base"></i>
              </div>
              <input
                type="text"
                name="pickupAddress"
                value={formData.pickupAddress}
                onChange={handleChange}
                className="w-full pl-9 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="Enter full pickup address"
              />
            </div>
          </div>

          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Drop-off Address
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-yellow-600">
                <i className="ri-map-pin-line text-base"></i>
              </div>
              <input
                type="text"
                name="dropoffAddress"
                value={formData.dropoffAddress}
                onChange={handleChange}
                className="w-full pl-9 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="Enter full drop-off address (e.g. clinic or hospital)"
              />
            </div>
          </div>

          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Notes for the Driver
            </label>
            <textarea
              name="notesForDriver"
              value={formData.notesForDriver}
              onChange={handleChange}
              maxLength={500}
              rows={4}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
              placeholder="Special instructions, mobility needs, gate codes, building access info, etc."
            ></textarea>
            <p className="text-xs text-gray-400 mt-1">{formData.notesForDriver.length}/500 characters</p>
          </div>

          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Current price you pay per ride (if applicable)
            </label>
            <select
              name="currentPrice"
              value={formData.currentPrice}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent cursor-pointer"
            >
              <option value="">Select price range</option>
              <option value="$80-$100">$80–$100</option>
              <option value="$100-$120">$100–$120</option>
              <option value="$120-$150">$120–$150</option>
              <option value="$150-$200">$150–$200</option>
              <option value="$200-$250">$200–$250</option>
              <option value="$250-$300">$250–$300</option>
              <option value="$300+">$300+</option>
            </select>
          </div>

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-300 rounded-lg flex items-center gap-3">
              <i className="ri-error-warning-fill text-red-500 text-xl"></i>
              <p className="text-red-700 font-medium">Please fill in all required fields and try again.</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-8 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black rounded-lg font-bold text-lg hover:from-yellow-400 hover:to-yellow-500 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer border-2 border-yellow-400"
          >
            {isSubmitting ? 'Submitting...' : 'Join the Early Access List'}
          </button>
        </form>
      </div>
    </section>
  );
}
